import { useEffect, useMemo, useRef, useState } from 'react';
import {
  KeyIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  UserPlusIcon,
  UserMinusIcon,
  BuildingOfficeIcon,
  TruckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Battery0Icon,
  Battery50Icon,
  Battery100Icon,
  BoltIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useFlinkeyBoxes, type FlinkeyBox, type FlinkeyNfcTag } from '@/hooks/useFlinkeyBoxes';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import { userSelectLabel } from '@/utils/userSearch';

interface TopNode { id: number; name: string; }

const CONTRACT_STATE_COLORS: Record<string, string> = {
  NEW: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  INACTIVE: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

export default function FlinkeyBoxesPage() {
  const { t } = useTranslation();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const h = useFlinkeyBoxes();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [deviaqDetail, setDeviaqDetail] = useState<FlinkeyBox | null>(null);

  // Vehicle link modal (reserverat för fordonskortet)
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [vehicleLinkTarget, setVehicleLinkTarget] = useState<number | null>(null);

  // Add user modal
  const [showAddUser, setShowAddUser] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<any[]>([]);

  // Toppnoder för dropdown
  const [topNodes, setTopNodes] = useState<TopNode[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | ''>('');
  const [orgFilter, setOrgFilter] = useState('');

  // NFC-taggar (visas/raderas i edit-modalen; registrering sker i appen)
  const [nfcTags, setNfcTags] = useState<FlinkeyNfcTag[]>([]);
  const [nfcLoading, setNfcLoading] = useState(false);

  useEffect(() => { h.fetchBoxes(); }, []);

  // Ladda NFC-taggar när edit-modalen öppnas
  useEffect(() => {
    if (!h.showEdit || !h.selected) { setNfcTags([]); return; }
    let cancelled = false;
    setNfcLoading(true);
    h.getNfcTags(h.selected.id)
      .then((tags) => { if (!cancelled) setNfcTags(tags); })
      .catch(() => { if (!cancelled) setNfcTags([]); })
      .finally(() => { if (!cancelled) setNfcLoading(false); });
    return () => { cancelled = true; };
  }, [h.showEdit, h.selected]);

  const handleDeleteNfcTag = async (tagId: number) => {
    if (!h.selected) return;
    try {
      await h.deleteNfcTag(h.selected.id, tagId);
      setNfcTags((prev) => prev.filter((t2) => t2.id !== tagId));
      toast.success(t('flinkeyBoxes.nfcTagDeleted'));
    } catch (e: any) {
      toast.error(e.response?.data?.error || t('flinkeyBoxes.nfcTagDeleteError'));
    }
  };

  // Hämta toppnoder en gång
  useEffect(() => {
    api.get('/api/admin/flinkey/top-nodes')
      .then(r => setTopNodes(r.data.data || []))
      .catch(() => {});
  }, []);

  // Vehicle search
  useEffect(() => {
    if (!vehicleSearch.trim() || vehicleSearch.length < 2) { setVehicles([]); return; }
    const id = setTimeout(async () => {
      try {
        const res = await api.get('/api/vehicles', { params: { search: vehicleSearch, limit: 20, withoutKeyOrFlinkey: 1 } });
        setVehicles(res.data.data?.vehicles || res.data.data || []);
      } catch { setVehicles([]); }
    }, 350);
    return () => clearTimeout(id);
  }, [vehicleSearch]);

  // User search
  useEffect(() => {
    if (!userSearch.trim() || userSearch.length < 2) { setUserResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const res = await api.get('/api/users', { params: { search: userSearch, limit: 20 } });
        setUserResults(res.data.data?.users || res.data.data || []);
      } catch { setUserResults([]); }
    }, 350);
    return () => clearTimeout(id);
  }, [userSearch]);

  const handleLinkVehicle = async (vehicleId: number) => {
    if (!vehicleLinkTarget) return;
    try {
      await h.linkVehicle(vehicleLinkTarget, vehicleId);
      toast.success(t('flinkeyBoxes.vehicleLinked'));
      setShowVehicleModal(false);
    } catch (e: any) {
      toast.error(e.response?.data?.error || t('flinkeyBoxes.vehicleLinkError'));
    }
  };

  const handleDeleteBox = (box: any) => {
    confirm({
      title: t('common.delete'),
      message: t('flinkeyBoxes.confirmDelete', { id: box.product_id }),
      variant: 'danger',
      onConfirm: () => h.handleDelete(box),
    });
  };

  const handleAddUser = async (user: any) => {
    if (!h.selected) return;
    try {
      await h.addUser(h.selected.id, String(user.id));
      setShowAddUser(false);
      setUserSearch('');
      setUserResults([]);
    } catch (e: any) {
      toast.error(e.response?.data?.error || t('flinkeyBoxes.userAddError'));
    }
  };

  const handleRemoveUser = (userId: string, name: string) => {
    if (!h.selected) return;
    const boxId = h.selected.id;
    confirm({
      title: t('flinkeyBoxes.removeUser'),
      message: t('flinkeyBoxes.confirmRemoveUser', { name }),
      variant: 'danger',
      onConfirm: () => h.removeUser(boxId, userId),
    });
  };

  const handleAssignOrg = async () => {
    if (!selectedOrgId) return;
    await h.assignOrganization(Number(selectedOrgId));
    setSelectedOrgId('');
  };

  const openAssignOrg = () => {
    setSelectedOrgId('');
    setOrgFilter('');
    h.setShowAssignOrg(true);
  };

  const filteredTopNodes = topNodes.filter(o =>
    !orgFilter.trim() || o.name.toLowerCase().includes(orgFilter.toLowerCase())
  );
  const selectedOrgName = topNodes.find(o => o.id === selectedOrgId)?.name ?? '';

  // Alla synliga boxar (för select-all)
  const visibleIds = h.boxes.map(b => b.id);

  const BoxForm = () => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {[
        { key: 'product_id', label: t('flinkeyBoxes.productId'), required: true },
        { key: 'serial_number', label: t('flinkeyBoxes.serialNumber'), required: true },
        { key: 'box_type', label: t('flinkeyBoxes.boxType') },
        { key: 'car_name', label: t('flinkeyBoxes.carName') },
        { key: 'license_plate', label: t('flinkeyBoxes.licensePlate') },
        { key: 'contract_start', label: t('flinkeyBoxes.contractStart'), type: 'date' },
        { key: 'contract_state', label: t('flinkeyBoxes.contractState') },
      ].map(({ key, label, required, type }) => (
        <div key={key}>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {label}{required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type={type || 'text'}
            value={(h.formData as any)[key]}
            onChange={e => h.setFormData(prev => ({ ...prev, [key]: e.target.value }))}
            className="w-full rounded-md border border-border bg-bgLight px-3 py-2 text-sm text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      ))}
      {/* Kopplat fordon (read-only info) */}
      {h.selected?.vehicle_registration && (
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('flinkeyBoxes.linkedVehicle') || 'Kopplat fordon'}
          </label>
          <div className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 dark:bg-green-900/20 px-3 py-2">
            <TruckIcon className="h-4 w-4 text-green-600 shrink-0" />
            <span className="text-sm font-medium text-green-800 dark:text-green-300">
              {h.selected.vehicle_registration}
              {h.selected.vehicle_name && (
                <span className="ml-2 font-normal text-green-600 dark:text-green-400">
                  {h.selected.vehicle_name}
                </span>
              )}
            </span>
          </div>
        </div>
      )}
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-textDefault mb-1">{t('flinkeyBoxes.notes')}</label>
        <textarea
          rows={2}
          value={h.formData.notes}
          onChange={e => h.setFormData(prev => ({ ...prev, notes: e.target.value }))}
          className="w-full rounded-md border border-border bg-bgLight px-3 py-2 text-sm text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {/* NFC test-läge */}
      <label className="flex items-start gap-3 sm:col-span-2 rounded-md border border-border bg-bgLight px-3 py-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!h.formData.nfc_check_enabled}
          onChange={e => h.setFormData(prev => ({ ...prev, nfc_check_enabled: !e.target.checked }))}
          className="mt-0.5 rounded border-border"
        />
        <span>
          <span className="block text-sm font-medium text-textDefault">{t('flinkeyBoxes.nfcTestMode')}</span>
          <span className="block text-xs text-textSecondary">{t('flinkeyBoxes.nfcTestModeHint')}</span>
        </span>
      </label>
      {/* Kortkontroll — kunden aktiverar */}
      <label className="flex items-start gap-3 sm:col-span-2 rounded-md border border-border bg-bgLight px-3 py-2 cursor-pointer">
        <input
          type="checkbox"
          checked={h.formData.nfc_card_check_enabled}
          onChange={e => h.setFormData(prev => ({ ...prev, nfc_card_check_enabled: e.target.checked }))}
          disabled={!h.formData.nfc_check_enabled}
          className="mt-0.5 rounded border-border"
        />
        <span>
          <span className="block text-sm font-medium text-textDefault">{t('flinkeyBoxes.nfcCardCheck')}</span>
          <span className="block text-xs text-textSecondary">{t('flinkeyBoxes.nfcCardCheckHint')}</span>
        </span>
      </label>
      {/* Detektera okända kort */}
      <label className="flex items-start gap-3 sm:col-span-2 rounded-md border border-border bg-bgLight px-3 py-2 cursor-pointer">
        <input
          type="checkbox"
          checked={h.formData.nfc_detect_enabled}
          onChange={e => h.setFormData(prev => ({ ...prev, nfc_detect_enabled: e.target.checked }))}
          className="mt-0.5 rounded border-border"
        />
        <span>
          <span className="block text-sm font-medium text-textDefault">{t('flinkeyBoxes.nfcDetectMode')}</span>
          <span className="block text-xs text-textSecondary">{t('flinkeyBoxes.nfcDetectModeHint')}</span>
        </span>
      </label>
      {/* Permanent ström */}
      <label className="flex items-start gap-3 sm:col-span-2 rounded-md border border-border bg-bgLight px-3 py-2 cursor-pointer">
        <input
          type="checkbox"
          checked={h.formData.has_permanent_power}
          onChange={e => h.setFormData(prev => ({ ...prev, has_permanent_power: e.target.checked }))}
          className="mt-0.5 rounded border-border"
        />
        <span>
          <span className="block text-sm font-medium text-textDefault">{t('flinkeyBoxes.permanentPower')}</span>
          <span className="block text-xs text-textSecondary">{t('flinkeyBoxes.permanentPowerHint')}</span>
        </span>
      </label>
    </div>
  );

  const colCount = 10;

  const renderBattery = (box: FlinkeyBox) => {
    const pct = box.last_battery_pct;
    if (pct == null && !box.charger_warning) {
      return <span className="text-textTertiary text-xs">—</span>;
    }
    const status = box.battery_status ?? 'unknown';
    const color =
      status === 'critical' ? 'text-red-600'
      : status === 'warning' ? 'text-orange-500'
      : 'text-green-600';
    const BatteryIcon = pct == null ? Battery0Icon : pct <= 20 ? Battery0Icon : pct <= 60 ? Battery50Icon : Battery100Icon;
    return (
      <div className="flex flex-col gap-0.5">
        <span className={`flex items-center gap-1 text-xs font-medium ${color}`}>
          <BatteryIcon className="h-4 w-4 shrink-0" />
          {pct != null ? `${pct}%` : t('flinkeyBoxes.batteryUnknown')}
          {box.last_battery_charging ? <BoltIcon className="h-3.5 w-3.5 text-green-500" title={t('flinkeyBoxes.batteryCharging')} /> : null}
        </span>
        {box.charger_warning ? (
          <span className="flex items-center gap-1 text-[11px] text-red-600" title={t('flinkeyBoxes.chargerDisconnected')}>
            <ExclamationTriangleIcon className="h-3 w-3 shrink-0" />
            {t('flinkeyBoxes.chargerDisconnected')}
          </span>
        ) : null}
      </div>
    );
  };

  const deviaqDetailRows = useMemo(() => {
    const raw = deviaqDetail?.deviaq_row_json;
    if (raw == null) return [] as { key: string; value: string }[];
    let obj: unknown = raw;
    if (typeof raw === 'string') {
      try {
        obj = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    if (!obj || typeof obj !== 'object') return [];
    return Object.keys(obj as Record<string, unknown>)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({
        key,
        value: String((obj as Record<string, unknown>)[key] ?? ''),
      }));
  }, [deviaqDetail]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <PageBackButton />
          <KeyIcon className="h-6 w-6 text-textSecondary" />
          <h1 className="text-xl font-semibold text-textDefault">{t('flinkeyBoxes.title')}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              setImportBusy(true);
              try {
                await h.importDeviaq(file);
              } catch (err: unknown) {
                const msg =
                  err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
                    : undefined;
                toast.error(typeof msg === 'string' ? msg : t('flinkeyBoxes.importDeviaqError'));
              } finally {
                setImportBusy(false);
              }
            }}
          />
          <button
            type="button"
            disabled={importBusy}
            onClick={() => importInputRef.current?.click()}
            className="flex items-center gap-2 rounded-lg border border-border bg-bgLight px-4 py-2 text-sm font-medium text-textDefault hover:bg-bgHover disabled:opacity-50"
          >
            {t('flinkeyBoxes.importDeviaq')}
          </button>
          <button
            type="button"
            onClick={h.openCreate}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <PlusCircleIcon className="h-5 w-5" />
            {t('flinkeyBoxes.addBox')}
          </button>
        </div>
      </div>

      {/* Bulk-toolbar — visas när boxar är markerade */}
      {h.selectedIds.size > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 px-4 py-3">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {h.selectedIds.size} {t('flinkeyBoxes.selectedBoxes')}
          </span>
          <button
            onClick={openAssignOrg}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            <BuildingOfficeIcon className="h-4 w-4" />
            {t('flinkeyBoxes.assignOrg')}
          </button>
          <button
            onClick={h.clearSelection}
            className="ml-auto text-xs text-blue-600 hover:text-blue-800 dark:text-blue-300"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-textSecondary" />
        <input
          type="text"
          placeholder={t('flinkeyBoxes.search')}
          value={h.search}
          onChange={e => h.setSearch(e.target.value)}
          className="w-full rounded-md border border-border bg-bgLight py-2 pl-9 pr-3 text-sm text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border bg-bgContainer">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-bgLight">
            <tr>
              {/* Select-all checkbox */}
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  checked={visibleIds.length > 0 && visibleIds.every(id => h.selectedIds.has(id))}
                  onChange={() => h.toggleSelectAll(visibleIds)}
                  className="rounded border-border"
                />
              </th>
              {[
                t('flinkeyBoxes.productId'),
                t('flinkeyBoxes.serialNumber'),
                t('flinkeyBoxes.boxType'),
                t('flinkeyBoxes.firmwareVersion'),
                t('flinkeyBoxes.battery'),
                t('flinkeyBoxes.organization') || 'Organisation',
                t('flinkeyBoxes.contractState'),
                t('flinkeyBoxes.individuals'),
                t('common.actions'),
              ].map(label => (
                <th key={label} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-textSecondary">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {h.loading ? (
              <tr><td colSpan={colCount} className="py-8 text-center text-textSecondary">{t('common.loading')}</td></tr>
            ) : h.boxes.length === 0 ? (
              <tr><td colSpan={colCount} className="py-8 text-center text-textSecondary">{t('flinkeyBoxes.noBoxes')}</td></tr>
            ) : h.boxes.map(box => (
              <tr
                key={box.id}
                className={`cursor-pointer hover:bg-bgLight/80 ${h.selectedIds.has(box.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                onClick={() => setDeviaqDetail(box)}
              >
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={h.selectedIds.has(box.id)}
                    onChange={() => h.toggleSelect(box.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-4 py-3 font-mono font-medium text-textDefault">{box.product_id}</td>
                <td className="px-4 py-3 text-textSecondary font-mono text-xs">{box.serial_number}</td>
                <td className="px-4 py-3 text-textSecondary">{box.box_type ?? '—'}</td>
                <td className="px-4 py-3 text-textSecondary text-xs font-mono">{box.firmware_version?.trim() ? box.firmware_version : '—'}</td>
                {/* Batteri */}
                <td className="px-4 py-3">{renderBattery(box)}</td>
                {/* Organisation */}
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {box.organization_id ? (
                    <span className="flex items-center gap-1 text-xs text-textSecondary">
                      <BuildingOfficeIcon className="h-3.5 w-3.5 shrink-0" />
                      {(box as any).organization_name || `Org ${box.organization_id}`}
                    </span>
                  ) : (
                    <button
                      onClick={() => { h.toggleSelect(box.id); openAssignOrg(); }}
                      className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-700"
                    >
                      <BuildingOfficeIcon className="h-3.5 w-3.5" />
                      {t('flinkeyBoxes.noOrg') || 'Saknar org'}
                    </button>
                  )}
                </td>
                {/* Kontraktstatus */}
                <td className="px-4 py-3">
                  {box.contract_state ? (
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${CONTRACT_STATE_COLORS[box.contract_state] ?? CONTRACT_STATE_COLORS.INACTIVE}`}>
                      {box.contract_state}
                    </span>
                  ) : '—'}
                </td>
                {/* Individer */}
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => h.openUsers(box)}
                    className="flex items-center gap-1 text-xs text-textSecondary hover:text-textDefault">
                    <UserPlusIcon className="h-4 w-4" />
                    {box.user_count ?? 0}
                  </button>
                </td>
                {/* Åtgärder */}
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Kopplat fordon */}
                    {box.vehicle_registration ? (
                      <span className="flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded px-1.5 py-0.5">
                        <TruckIcon className="h-3.5 w-3.5 shrink-0" />
                        {box.vehicle_registration}
                        <button
                          onClick={() => h.unlinkVehicle(box.id)}
                          title={t('flinkeyBoxes.unlinkVehicle') || 'Koppla loss fordon'}
                          className="ml-0.5 text-green-600 hover:text-red-600"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => { setVehicleLinkTarget(box.id); setShowVehicleModal(true); }}
                        title={t('flinkeyBoxes.linkVehicle') || 'Koppla fordon'}
                        className="flex items-center gap-1 text-xs text-textSecondary hover:text-blue-600 border border-dashed border-border rounded px-1.5 py-0.5 hover:border-blue-400"
                      >
                        <TruckIcon className="h-3.5 w-3.5" />
                        {t('flinkeyBoxes.linkVehicle') || 'Koppla fordon'}
                      </button>
                    )}
                    <button onClick={() => h.openEdit(box)} title={t('common.edit')}
                      className="text-textSecondary hover:text-blue-600">
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDeleteBox(box)} title={t('common.delete')}
                      className="text-textSecondary hover:text-red-600">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assign Org modal — dropdown med toppnoder */}
      {h.showAssignOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-xl bg-bgContainer p-6 shadow-xl">
            <h2 className="mb-1 text-lg font-semibold text-textDefault">{t('flinkeyBoxes.assignOrgTitle')}</h2>
            <p className="mb-4 text-sm text-textSecondary">{h.selectedIds.size} {t('flinkeyBoxes.selectedBoxes')}</p>

            <label className="block text-sm font-medium text-textDefault mb-1">{t('flinkeyBoxes.assignOrgTopNode')}</label>

            {/* Vald org */}
            {selectedOrgId && (
              <div className="flex items-center gap-2 mb-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 px-3 py-2">
                <BuildingOfficeIcon className="h-4 w-4 text-blue-600 shrink-0" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200 flex-1">{selectedOrgName}</span>
                <button onClick={() => setSelectedOrgId('')} className="text-blue-400 hover:text-blue-700">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            )}

            {/* Sökfält */}
            <div className="relative mb-1">
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-textSecondary" />
              <input
                autoFocus
                type="text"
                placeholder={t('flinkeyBoxes.searchOrg')}
                value={orgFilter}
                onChange={e => setOrgFilter(e.target.value)}
                className="w-full rounded-md border border-border bg-bgLight py-2 pl-9 pr-3 text-sm text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Lista */}
            <div className="max-h-52 overflow-y-auto rounded-md border border-border divide-y divide-border">
              {filteredTopNodes.length === 0 ? (
                <p className="px-3 py-3 text-sm text-textSecondary">{t('flinkeyBoxes.noOrgMatches')}</p>
              ) : filteredTopNodes.map(o => (
                <button
                  key={o.id}
                  onClick={() => setSelectedOrgId(o.id)}
                  className={`w-full text-left px-3 py-2.5 text-sm transition-colors ${
                    selectedOrgId === o.id
                      ? 'bg-blue-600 text-white'
                      : 'text-textDefault hover:bg-bgLight'
                  }`}
                >
                  {o.name}
                </button>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => { h.setShowAssignOrg(false); setSelectedOrgId(''); }}
                className="rounded-lg border border-border px-4 py-2 text-sm text-textDefault hover:bg-bgLight"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAssignOrg}
                disabled={!selectedOrgId}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {h.showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg max-h-[min(92vh,calc(100dvh-2rem))] overflow-hidden rounded-xl bg-bgContainer shadow-xl flex flex-col">
            <h2 className="shrink-0 px-6 pt-6 pb-2 text-lg font-semibold text-textDefault">{t('flinkeyBoxes.addBox')}</h2>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-2">
              {BoxForm()}
            </div>
            <div className="shrink-0 border-t border-border px-6 py-4 flex justify-end gap-3">
              <button onClick={() => h.setShowCreate(false)} className="rounded-lg border border-border px-4 py-2 text-sm text-textDefault hover:bg-bgLight">{t('common.cancel')}</button>
              <button onClick={h.handleCreate} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {h.showEdit && h.selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg max-h-[min(92vh,calc(100dvh-2rem))] overflow-hidden rounded-xl bg-bgContainer shadow-xl flex flex-col">
            <h2 className="shrink-0 px-6 pt-6 pb-2 text-lg font-semibold text-textDefault">{t('flinkeyBoxes.editBox')} — {h.selected.product_id}</h2>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-2">
              {BoxForm()}

              {/* Registrerade NFC-taggar (nyckel/kort) */}
              <div className="mt-5 border-t border-border pt-4 pb-2">
                <h3 className="text-sm font-semibold text-textDefault mb-1">{t('flinkeyBoxes.nfcTagsTitle')}</h3>
                <p className="text-xs text-textSecondary mb-2">{t('flinkeyBoxes.nfcTagsHint')}</p>
                {nfcLoading ? (
                  <p className="text-xs text-textSecondary py-2">{t('common.loading')}</p>
                ) : nfcTags.length === 0 ? (
                  <p className="text-xs text-textSecondary py-2">{t('flinkeyBoxes.noNfcTags')}</p>
                ) : (
                  <div className="divide-y divide-border rounded-md border border-border">
                    {nfcTags.map((tag) => (
                      <div key={tag.id} className="flex items-center justify-between px-3 py-2">
                        <div className="min-w-0">
                          <span className="font-mono text-xs text-textDefault break-all">{tag.nfc_uid}</span>
                          <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-textSecondary">
                            {(() => {
                              const n = String(tag.label || '').trim().toLowerCase();
                              if (n === 'card' || n === 'kort') return t('flinkeyBoxes.nfcLabelCard');
                              return t('flinkeyBoxes.nfcLabelKey');
                            })()}
                            {tag.is_required ? (
                              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                {t('flinkeyBoxes.nfcRequired')}
                              </span>
                            ) : null}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteNfcTag(tag.id)}
                          title={t('common.delete')}
                          className="shrink-0 text-textTertiary hover:text-red-500"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="shrink-0 border-t border-border px-6 py-4 flex justify-end gap-3">
              <button onClick={() => h.setShowEdit(false)} className="rounded-lg border border-border px-4 py-2 text-sm text-textDefault hover:bg-bgLight">{t('common.cancel')}</button>
              <button onClick={h.handleUpdate} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle link modal */}
      {showVehicleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-bgContainer p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-textDefault">{t('flinkeyBoxes.linkVehicleTitle')}</h2>
            <input
              autoFocus
              type="text"
              placeholder={t('flinkeyBoxes.searchVehicle')}
              value={vehicleSearch}
              onChange={e => setVehicleSearch(e.target.value)}
              className="w-full rounded-md border border-border bg-bgLight px-3 py-2 text-sm text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="mt-3 max-h-60 overflow-y-auto divide-y divide-border">
              {vehicles.map((v: any) => (
                <button key={v.id} onClick={() => handleLinkVehicle(v.id)}
                  className="w-full text-left px-3 py-2.5 hover:bg-bgLight text-sm text-textDefault">
                  <span className="font-medium">{v.registration_number}</span>
                  <span className="ml-2 text-textSecondary">{v.brand} {v.model}</span>
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setShowVehicleModal(false)} className="rounded-lg border border-border px-4 py-2 text-sm text-textDefault hover:bg-bgLight">{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Users modal */}
      {h.showUsers && h.selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg max-h-[min(92vh,calc(100dvh-2rem))] overflow-hidden rounded-xl bg-bgContainer shadow-xl flex flex-col">
            <div className="shrink-0 px-6 pt-6">
            <h2 className="mb-1 text-lg font-semibold text-textDefault">{t('flinkeyBoxes.individuals')} — {h.selected.product_id}</h2>
            <p className="mb-4 text-sm text-textSecondary">{t('flinkeyBoxes.individualsDescription')}</p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6">

            {showAddUser ? (
              <div className="mb-4">
                <input
                  autoFocus
                  type="text"
                  placeholder={t('flinkeyBoxes.searchUser')}
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  className="w-full rounded-md border border-border bg-bgLight px-3 py-2 text-sm text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-2 max-h-40 overflow-y-auto divide-y divide-border rounded-md border border-border">
                  {userResults.map((u: any) => (
                    <button key={u.id} onClick={() => handleAddUser(u)}
                      className="w-full text-left px-3 py-2 hover:bg-bgLight text-sm text-textDefault">
                      <span className="font-medium">{userSelectLabel(u)}</span>
                      <span className="ml-2 text-textSecondary">{u.email}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => { setShowAddUser(false); setUserSearch(''); setUserResults([]); }}
                  className="mt-2 text-xs text-textSecondary hover:text-textDefault">
                  {t('common.cancel')}
                </button>
              </div>
            ) : (
              <button onClick={() => setShowAddUser(true)}
                className="mb-4 flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-textSecondary hover:text-textDefault hover:border-textSecondary w-full">
                <UserPlusIcon className="h-4 w-4" />
                {t('flinkeyBoxes.addUser')}
              </button>
            )}

            <div className="divide-y divide-border max-h-60 overflow-y-auto">
              {h.loadingUsers ? (
                <p className="py-4 text-center text-sm text-textSecondary">{t('common.loading')}</p>
              ) : h.boxUsers.length === 0 ? (
                <p className="py-4 text-center text-sm text-textSecondary">{t('flinkeyBoxes.noUsers')}</p>
              ) : h.boxUsers.map(u => (
                <div key={u.user_id} className="flex items-center justify-between py-2.5">
                  <div>
                    <span className="text-sm font-medium text-textDefault">{u.first_name} {u.last_name}</span>
                    <span className="ml-2 text-xs text-textSecondary">{u.email}</span>
                  </div>
                  <button onClick={() => handleRemoveUser(u.user_id, `${u.first_name} ${u.last_name}`)}
                    className="text-textTertiary hover:text-red-500" title={t('flinkeyBoxes.removeUser')}>
                    <UserMinusIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            </div>
            <div className="shrink-0 border-t border-border px-6 py-4 flex justify-end">
              <button onClick={() => h.setShowUsers(false)} className="rounded-lg border border-border px-4 py-2 text-sm text-textDefault hover:bg-bgLight">{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {deviaqDetail && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDeviaqDetail(null)}
        >
          <div
            className="w-full max-w-lg max-h-[85vh] overflow-hidden rounded-xl bg-bgContainer shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-lg font-semibold text-textDefault">
                {t('flinkeyBoxes.deviaqDetailsTitle')} — {deviaqDetail.product_id}
              </h2>
              <button
                type="button"
                onClick={() => setDeviaqDetail(null)}
                className="rounded p-1 text-textSecondary hover:bg-bgLight"
                aria-label={t('common.close')}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 text-sm">
              {deviaqDetailRows.length === 0 ? (
                <p className="text-textSecondary">{t('flinkeyBoxes.deviaqNoDetails')}</p>
              ) : (
                <dl className="space-y-2">
                  {deviaqDetailRows.map(({ key, value }) => (
                    <div key={key} className="grid grid-cols-1 gap-0.5 sm:grid-cols-3 sm:gap-2 border-b border-border/60 pb-2">
                      <dt className="font-medium text-textSecondary break-all">{key}</dt>
                      <dd className="sm:col-span-2 text-textDefault break-words whitespace-pre-wrap">{value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
            <div className="border-t border-border px-4 py-3 flex justify-end">
              <button
                type="button"
                onClick={() => setDeviaqDetail(null)}
                className="rounded-lg border border-border px-4 py-2 text-sm text-textDefault hover:bg-bgLight"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog />
    </div>
  );
}
